import React, { useEffect } from "react";
import { MathUtils } from "three";
import getDefaultModelPic from "../../shared/components/assets/getDefaultModelPic";
import { useClient } from "../../shared/hooks/client-hook";
import { db, storage } from "../../shared/hooks/firebase";

const ModelPanel = () => {
  const { client, editor, currentTool, selected, setSelected } = useClient();

  function setClient(newClient) {
    db.ref("/client").set(newClient);
  }

  function addModel() {
    const id = MathUtils.generateUUID();

    //open file input accepting gltf and glb files

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".gltf, .glb";
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        console.log(dataUrl);

        storage
          .ref("assets/models")
          .child(id + ".gltf")
          .putString(dataUrl, "data_url")

          .then((snapshot) => {
            storage
              .ref(snapshot.ref.fullPath)
              .getDownloadURL()
              .then((url) => {
                console.log(url);

                const newModel = {
                  id: id,
                  url: url,
                  name: file.name,
                  category: currentTool,
                  image: getDefaultModelPic(),
                };

                const newEditor = { ...editor };

                if (!newEditor.models) {
                  newEditor.models = {};
                }

                if (!newEditor.models[currentTool]) {
                  newEditor.models[currentTool] = {};
                }

                newEditor.models[currentTool][id] = newModel;

                db.ref("/editor").set(newEditor);
              });
          });
      };

      reader.readAsDataURL(file);
    };
    fileInput.click();
  }

  function createInstance(model) {
    const id = MathUtils.generateUUID();

    const instance = {
      id,
      name: model.name,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      model: model.id,
    };

    const newClient = { ...client };

    if (!newClient.models) {
      newClient.models = {};
    }

    if (!newClient.models[currentTool]) {
      newClient.models[currentTool] = {};
    }

    newClient.models[currentTool][id] = instance;

    db.ref("/client").set(newClient);
  }

  function deleteInstance(instance) {
    const newClient = { ...client };

    if (newClient.models[currentTool][instance.id]) {
      delete newClient.models[currentTool][instance.id];

      db.ref("/client").set(newClient);
    }
  }

  function deleteModel(model) {
    const newEditor = { ...editor };
    const newClient = { ...client };

    if (newEditor.models[currentTool][model.id]) {
      delete newEditor.models[currentTool][model.id];
    }

    const newInstances = {};

    Object.values(newClient.models[currentTool]).map((instance) => {
      if (instance.model !== model.id) {
        newInstances[instance.id] = instance;
      }
    });

    newClient.models[currentTool] = newInstances;

    db.ref("/editor").set(newEditor);
    db.ref("/client").set(newClient);
  }

  function renderInput(property) {
    switch (property) {
      case "isFloor":
        return null;

      case "position":
        return (
          <Vector3Input
            selected={selected}
            property={"position"}
            client={client}
            modelType={currentTool}
            setClient={setClient}
          />
        );

      case "scale":
        return (
          <Vector3Input
            selected={selected}
            property={"scale"}
            client={client}
            modelType={currentTool}
            setClient={setClient}
          />
        );

      case "rotation":
        return (
          <Vector3Input
            selected={selected}
            property={"rotation"}
            client={client}
            modelType={currentTool}
            setClient={setClient}
          />
        );

      case "model":
        return (
          <select
            className="bg-gray-500 text-white w-full col-span-2"
            value={selected[property]}
            onChange={(e) => {
              const newValue = e.target.value;

              const newClient = { ...client };

              const newBuilding = {
                ...newClient.models[currentTool][selected.id],
              };

              newBuilding.model = newValue;

              newClient.models[currentTool][selected.id] = newBuilding;

              setClient(newClient);
              setSelected(newBuilding);
            }}
          >
            {Object.values(editor.models[currentTool]).map((model) => {
              return (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              );
            })}
          </select>
        );

      default:
        return (
          <input
            className="bg-gray-500 text-white p-2 col-span-2"
            value={selected[property]}
            onChange={(e) => {
              const newValue = e.target.value;

              const newClient = { ...client };

              newClient.models[currentTool][selected.id][property] = newValue;

              setClient(newClient);
            }}
          />
        );
    }
  }

  function toggleFloor() {
    const newClient = { ...client };

    newClient.models[currentTool][selected.id].isFloor = !selected.isFloor;

    db.ref("/client").set(newClient);
  }

  useEffect(() => {
    if (selected) {
      const selectedModel = client.models[currentTool][selected.id];

      if (selectedModel) {
        setSelected(selectedModel);
        return;
      }
      setSelected(null);
    }
  }, [client, selected]);

  return (
    <div className="flex-1 bg-gray-50 p-2 text-gray-500 relative overflow-y-auto w-[445px]">
      <h3>
        {editor && editor.modelTabs && editor.modelTabs[currentTool]
          ? editor.modelTabs[currentTool].name
          : "loading"}
      </h3>

      <div className="border min-h-[25vh] shadow-sm rounded-md my-4">
        {client &&
          client.models &&
          client.models[currentTool] &&
          Object.values(client.models[currentTool]).map((instance) => {
            return (
              <div className="w-full relative border-b" key={instance.id}>
                <button
                  onClick={() => {
                    setSelected(instance);
                  }}
                  className={`p-2 ${
                    selected && selected.id === instance.id
                      ? "text-blue-500"
                      : "text-gray-800"
                  }`}
                >
                  {instance.name}
                </button>

                <button
                  onClick={() => {
                    deleteInstance(instance);
                  }}
                  className="absolute italics text-red-500 right-4 p-2"
                >
                  delete
                </button>
              </div>
            );
          })}
      </div>

      {!selected && (
        <>
          <h3>Assets</h3>

          <div className="grid grid-cols-4 my-4 gap-2">
            <button
              onClick={addModel}
              className="bg-gray-500 w-full aspect-square rounded-md shadow-sm text-white flex items-center"
            >
              <span className="m-auto">Add</span>
            </button>

            {editor &&
              editor.models &&
              editor.models[currentTool] &&
              Object.values(editor.models[currentTool]).map((model, index) => {
                return (
                  <div
                    key={model.id}
                    className="group relative bg-gray-500 w-full aspect-square rounded-md shadow-sm text-white flex items-center mb-10"
                  >
                    <div className="absolute bg-black p-2 text-white left-1/2 -translate-x-1/2 text-xs -top-10 hidden group-hover:block z-10">
                      {model.name}
                    </div>

                    <div className="absolute -bottom-8 w-full grid grid-cols-3">
                      <button
                        className="bg-green-500"
                        onClick={() => {
                          createInstance(model);
                        }}
                      >
                        +
                      </button>

                      <button
                        onClick={() => {
                          deleteModel(model);
                        }}
                        className="bg-red-500"
                      >
                        x
                      </button>

                      <button
                        onClick={() => {
                          createInstance(model);
                        }}
                        className="bg-blue-500"
                      >
                        U
                      </button>
                    </div>

                    <img
                      src={model.image}
                      alt="model"
                      className="object-cover rounded-md"
                    />
                  </div>
                );
              })}
          </div>
        </>
      )}
      {selected && (
        <>
          <div className="w-full relative flex">
            <div className={`p-2`}>{selected.name}</div>

            <button
              onClick={() => {
                setSelected(null);
              }}
              className="absolute italics text-red-500 right-4 p-2"
            >
              unselect
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 ">
            {Object.keys(selected).map((key) => {
              if (key === "id" || key === "isFloor") {
                return null;
              }

              return (
                <React.Fragment key={key}>
                  <div className="text-right">{key}:</div>

                  {renderInput(key)}
                </React.Fragment>
              );
            })}

            <div></div>
            <p>Floor:</p>
            <div>
              <button
                onClick={() => {
                  toggleFloor();
                }}
                className={`${
                  selected.isFloor ? "bg-blue-500 " : "bg-white "
                } border w-6 h-6 rounded-lg`}
              ></button>
            </div>

            <div className="col-span-3"></div>
            <div className="col-span-3"></div>
            <div className="col-span-3"></div>

            <div></div>

            <button
              onClick={deleteInstance}
              className="w-full py-2 text-center bg-red-500 rounded-sm col-span-2 text-white"
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const Vector3Input = ({ selected, property, client, modelType, setClient }) => {
  return (
    <div className="col-span-2 grid grid-cols-3 gap-3">
      <PropInput
        selected={selected}
        property={property}
        client={client}
        modelType={modelType}
        setClient={setClient}
        channels={true}
        channel={0}
        className={`text-red-300`}
      />
      <PropInput
        selected={selected}
        property={property}
        client={client}
        modelType={modelType}
        setClient={setClient}
        channels={true}
        channel={1}
        className={`text-green-300`}
      />
      <PropInput
        selected={selected}
        property={property}
        client={client}
        modelType={modelType}
        setClient={setClient}
        channels={true}
        channel={2}
        className={`text-blue-300`}
      />
    </div>
  );
};

const PropInput = ({
  selected,
  property,
  client,
  modelType,
  setClient,
  channels = false,
  channel,
  className,
}) => {
  return (
    <input
      className={`${className} bg-gray-500 w-full p-2`}
      value={channels ? selected[property][channel] : selected[property]}
      type={"number"}
      lang="en"
      step={0.1}
      onChange={(e) => {
        const newValue = e.target.value;

        const newClient = { ...client };

        if (channels) {
          newClient.models[modelType][selected.id][property][channel] =
            parseFloat(newValue);

          setClient(newClient);
        } else {
          newClient.models[modelType][selected.id][property] =
            parseFloat(newValue);

          setClient(newClient);
        }
      }}
    />
  );
};

export default ModelPanel;
